import { useState } from "react";
import { useNavigate } from "react-router-dom";

interface ToggleState {
  whatIsLiveSale: boolean;
  howToSell: boolean;
  notification: boolean;
  feesAndSettlement: boolean;
  cancelOrder: boolean;
}

interface Question {
  key: keyof ToggleState;
  title: string;
  element: JSX.Element;
}

const OnboardingLiveSales = () => {
  const navigate = useNavigate();
  const [toggles, setToggles] = useState<ToggleState>({
    whatIsLiveSale: false,
    howToSell: false,
    notification: false,
    feesAndSettlement: false,
    cancelOrder: false,
  });

  const handleToggle = (key: keyof ToggleState) => {
    setToggles((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleClick = () => {
    navigate("/boss/onboarding/complete");
  };

  const questions: Question[] = [
    {
      key: "whatIsLiveSale",
      title: "실시간 알림 판매가 뭔가요?",
      element: (
        <div className="space-y-3">
          <p className="text-gray-800 leading-relaxed">
            사장님께서 당일 남은 상품을 직접 업로드하면, 주변 손님들에게 알림이
            갑니다.
          </p>
          <p className="text-gray-800">
            손님들이 앱에서 결제 후 매장에서 픽업하게 됩니다.
          </p>
          <p className="text-primary font-medium">
            미리 메뉴를 등록해두면 더욱 쉽게 판매할 수 있습니다!
          </p>
        </div>
      ),
    },
    {
      key: "howToSell",
      title: "어떻게 판매하나요?",
      element: (
        <div className="space-y-4">
          <p className="text-gray-800 leading-relaxed">
            상품을 업로드하면 근처 손님이 알림을 받고 앱에서 결제 후 픽업합니다.
          </p>
          <div className="bg-blue-50 p-4 rounded-xl">
            <p className="font-medium text-blue-800">판매 과정</p>
            <ul className="mt-2 space-y-2 text-blue-700">
              <li>1. 남은 상품 업로드</li>
              <li>2. 주변 손님들에게 알림 발송</li>
              <li>3. 손님이 앱에서 결제</li>
              <li>4. 예약 정보 확인 (닉네임, 연락처, 도착 예정 시간)</li>
              <li>5. 방문 시 상품 전달</li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      key: "notification",
      title: "알림은 어떻게 가나요?",
      element: (
        <div className="space-y-3">
          <p className="text-gray-800 leading-relaxed">
            상품 업로드 시 <span className="font-medium">반경 3km 이내</span>의
            손님들에게 자동으로 알림이 발송됩니다.
          </p>
          <p className="text-primary font-medium">
            관심 매장으로 등록한 손님들에게는 더 빠르게 알림이 발송됩니다!
          </p>
        </div>
      ),
    },
    {
      key: "feesAndSettlement",
      title: "수수료 및 정산은 어떻게 되나요?",
      element: (
        <div className="space-y-3">
          <p className="text-gray-800 leading-relaxed">
            <span className="text-primary font-medium">
              수수료는 할인가의 9% 고정
            </span>
          </p>
          <p className="text-gray-800">
            정산은 전월 거래내역 기준으로 월 1회 자동 정산되며, 거래내역은
            부가세 신고 참고자료로 활용하실 수 있습니다.
          </p>
        </div>
      ),
    },
    {
      key: "cancelOrder",
      title: "주문 취소는 어떻게 하나요?",
      element: (
        <div className="space-y-3">
          <p className="text-gray-800 leading-relaxed">
            픽업 예정 시간 <span className="font-medium">30분 전까지</span>{" "}
            자유롭게 주문 취소가 가능합니다.
          </p>
          <p className="text-primary font-medium">
            취소 시 자동으로 고객에게 알림이 가고, 결제 금액은 즉시 환불됩니다.
          </p>
        </div>
      ),
    },
  ];

  return (
    <div className="w-full h-[100dvh] overflow-y-auto max-w-[480px] mx-auto bg-white">
      <div className="flex flex-col p-4 w-full space-y-6">
        <div className="space-y-2">
          <h1 className="text-4xl font-bold bg-blue-500 text-white inline px-4">
            실시간 알림 판매
          </h1>
          <p className="text-gray-600 text-3xl">
            아래 질문들을 클릭해서 살펴볼까요?
          </p>
        </div>

        <div className="space-y-3">
          {questions.map(({ key, title, element }) => (
            <div
              key={key}
              className="rounded-xl border shadow overflow-hidden bg-white"
            >
              <button
                onClick={() => handleToggle(key as keyof ToggleState)}
                className="w-full p-4 flex justify-between items-center active:bg-gray-50 transition-colors"
              >
                <span className="font-bold text-gray-800 text-left text-3xl">
                  {title}
                </span>
                <svg
                  className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                    toggles[key as keyof ToggleState] ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {toggles[key as keyof ToggleState] && (
                <div className="p-4 bg-gray-50 border-t border-gray-200 text-3xl">
                  {element}
                </div>
              )}
            </div>
          ))}
        </div>
        <button
          onClick={handleClick}
          className="w-full py-3.5 bg-primary text-white font-semibold rounded-2xl transition duration-150 active:brightness-90 pointer-events-auto"
        >
          다음
        </button>
      </div>
    </div>
  );
};

export default OnboardingLiveSales;
